import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import AlertBanner from './components/AlertBanner';
import ProductionAreaTile from './components/ProductionAreaTile';
import TimelineView from './components/TimelineView';
import FilterPanel from './components/FilterPanel';
import SystemIntegrationStatus from './components/SystemIntegrationStatus';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import productionService from '../../services/productionService';
import realtimeService from '../../services/realtimeService';
import WorkerAssignmentPanel from './components/WorkerAssignmentPanel';
import EquipmentConnectivityPanel from './components/EquipmentConnectivityPanel';

const RealTimeProductionMonitoring = () => {
  const { projectId } = useParams();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [alerts, setAlerts] = useState([]);
  const [productionAreas, setProductionAreas] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState([]);
  const [productionLines, setProductionLines] = useState([]);

  const [filters, setFilters] = useState({
    productionLine: 'all',
    shift: 'all',
    status: 'all',
    severity: 'all',
    searchQuery: '',
    minCompletion: '',
    maxCompletion: ''
  });

  const [milestones] = useState([
    {
      id: 1,
      title: 'Morning Shift Start',
      scheduledTime: new Date(2026, 0, 13, 6, 0),
      status: 'completed',
      progress: 100,
      unitsProduced: 1250,
      targetUnits: 1250,
      workersAssigned: 45
    },
    {
      id: 2,
      title: 'First Quality Check',
      scheduledTime: new Date(2026, 0, 13, 8, 30),
      status: 'completed',
      progress: 100,
      unitsProduced: 890,
      targetUnits: 890,
      workersAssigned: 12
    },
    {
      id: 3,
      title: 'Mid-Morning Target',
      scheduledTime: new Date(2026, 0, 13, 10, 0),
      status: 'in-progress',
      progress: 78,
      unitsProduced: 1560,
      targetUnits: 2000,
      workersAssigned: 52
    },
    {
      id: 4,
      title: 'Lunch Break Start',
      scheduledTime: new Date(2026, 0, 13, 12, 0),
      status: 'upcoming',
      progress: 0,
      unitsProduced: 0,
      targetUnits: 0,
      workersAssigned: 0
    },
    {
      id: 5,
      title: 'Afternoon Production',
      scheduledTime: new Date(2026, 0, 13, 13, 0),
      status: 'upcoming',
      progress: 0,
      unitsProduced: 0,
      targetUnits: 2500,
      workersAssigned: 48
    },
    {
      id: 6,
      title: 'Shift End Target',
      scheduledTime: new Date(2026, 0, 13, 14, 0),
      status: 'upcoming',
      progress: 0,
      unitsProduced: 0,
      targetUnits: 5000,
      workersAssigned: 45
    }
  ]);

  const [integrations] = useState([
    {
      id: 1,
      name: 'MES System',
      icon: 'Database',
      status: 'connected',
      latency: 45,
      lastSync: '30s ago',
      dataStreams: ['Production', 'Quality', 'Inventory']
    },
    {
      id: 2,
      name: 'ERP Integration',
      icon: 'Server',
      status: 'connected',
      latency: 120,
      lastSync: '1m ago',
      dataStreams: ['Orders', 'Materials', 'Scheduling']
    },
    {
      id: 3,
      name: 'Equipment Sensors',
      icon: 'Activity',
      status: 'warning',
      latency: 890,
      lastSync: '5m ago',
      dataStreams: ['Temperature', 'Vibration', 'Status']
    },
    {
      id: 4,
      name: 'Quality Systems',
      icon: 'ClipboardCheck',
      status: 'connected',
      latency: 67,
      lastSync: '45s ago',
      dataStreams: ['Inspections', 'Defects', 'Compliance']
    },
    {
      id: 5,
      name: 'Worker Tracking',
      icon: 'Users',
      status: 'connected',
      latency: 34,
      lastSync: '20s ago',
      dataStreams: ['Attendance', 'Tasks', 'Performance']
    },
    {
      id: 6,
      name: 'Maintenance System',
      icon: 'Wrench',
      status: 'connected',
      latency: 156,
      lastSync: '2m ago',
      dataStreams: ['Schedules', 'Work Orders', 'Parts']
    }
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const refreshTimer = setInterval(() => {
        setLastRefresh(new Date());
      }, 30000);

      return () => clearInterval(refreshTimer);
    }
  }, [autoRefresh]);

  // Load initial data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [areasData, alertsData, workersData, shiftsData, taskAssignmentsData, productionLinesData] = await Promise.all([
          productionService?.getProductionAreas(),
          productionService?.getAlerts(),
          productionService?.getWorkers(),
          productionService?.getShifts(),
          productionService?.getTaskAssignments(),
          productionService?.getProductionLines()
        ]);
        setProductionAreas(areasData);
        setAlerts(alertsData);
        setWorkers(workersData);
        setShifts(shiftsData);
        setTaskAssignments(taskAssignmentsData);
        setProductionLines(productionLinesData);
        setLastRefresh(new Date());
      } catch (err) {
        setError(err?.message || 'Failed to load production data');
        console.error('Error loading production data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Real-time subscriptions
  useEffect(() => {
    const channels = [];

    // Subscribe to production areas changes
    const areasChannel = realtimeService?.subscribeToProductionAreas((payload) => {
      if (payload?.eventType === 'INSERT') {
        setProductionAreas(prev => [...prev, { ...payload?.new, equipment: [] }]);
      } else if (payload?.eventType === 'UPDATE') {
        setProductionAreas(prev => prev?.map(area => 
          area?.id === payload?.new?.id ? { ...area, ...payload?.new } : area
        ));
      } else if (payload?.eventType === 'DELETE') {
        setProductionAreas(prev => prev?.filter(area => area?.id !== payload?.old?.id));
      }
      setLastRefresh(new Date());
    });
    channels?.push(areasChannel);

    // Subscribe to equipment changes
    const equipmentChannel = realtimeService?.subscribeToEquipment((payload) => {
      if (payload?.eventType === 'INSERT') {
        setProductionAreas(prev => prev?.map(area => {
          if (area?.id === payload?.new?.production_area_id) {
            return {
              ...area,
              equipment: [...(area?.equipment || []), payload?.new]
            };
          }
          return area;
        }));
      } else if (payload?.eventType === 'UPDATE') {
        setProductionAreas(prev => prev?.map(area => ({
          ...area,
          equipment: area?.equipment?.map(eq => 
            eq?.id === payload?.new?.id ? payload?.new : eq
          ) || []
        })));
      } else if (payload?.eventType === 'DELETE') {
        setProductionAreas(prev => prev?.map(area => ({
          ...area,
          equipment: area?.equipment?.filter(eq => eq?.id !== payload?.old?.id) || []
        })));
      }
      setLastRefresh(new Date());
    });
    channels?.push(equipmentChannel);

    // Subscribe to alerts changes
    const alertsChannel = realtimeService?.subscribeToAlerts((payload) => {
      if (payload?.eventType === 'INSERT') {
        setAlerts(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setAlerts(prev => prev?.map(alert => 
          alert?.id === payload?.new?.id ? payload?.new : alert
        ));
      } else if (payload?.eventType === 'DELETE') {
        setAlerts(prev => prev?.filter(alert => alert?.id !== payload?.old?.id));
      }
      setLastRefresh(new Date());
    });
    channels?.push(alertsChannel);

    // Subscribe to workers changes
    const workersChannel = realtimeService?.subscribeToWorkers((payload) => {
      if (payload?.eventType === 'INSERT') {
        setWorkers(prev => [...prev, payload?.new]);
      } else if (payload?.eventType === 'UPDATE') {
        setWorkers(prev => prev?.map(worker => 
          worker?.id === payload?.new?.id ? payload?.new : worker
        ));
      } else if (payload?.eventType === 'DELETE') {
        setWorkers(prev => prev?.filter(worker => worker?.id !== payload?.old?.id));
      }
      setLastRefresh(new Date());
    });
    channels?.push(workersChannel);

    // Subscribe to shifts changes
    const shiftsChannel = realtimeService?.subscribeToShifts((payload) => {
      if (payload?.eventType === 'INSERT') {
        setShifts(prev => [...prev, payload?.new]);
      } else if (payload?.eventType === 'UPDATE') {
        setShifts(prev => prev?.map(shift => 
          shift?.id === payload?.new?.id ? payload?.new : shift
        ));
      } else if (payload?.eventType === 'DELETE') {
        setShifts(prev => prev?.filter(shift => shift?.id !== payload?.old?.id));
      }
      setLastRefresh(new Date());
    });
    channels?.push(shiftsChannel);

    // Subscribe to task assignments changes
    const taskAssignmentsChannel = realtimeService?.subscribeToTaskAssignments((payload) => {
      if (payload?.eventType === 'INSERT') {
        setTaskAssignments(prev => [...prev, payload?.new]);
      } else if (payload?.eventType === 'UPDATE') {
        setTaskAssignments(prev => prev?.map(task => 
          task?.id === payload?.new?.id ? payload?.new : task
        ));
      } else if (payload?.eventType === 'DELETE') {
        setTaskAssignments(prev => prev?.filter(task => task?.id !== payload?.old?.id));
      }
      setLastRefresh(new Date());
    });
    channels?.push(taskAssignmentsChannel);

    // Subscribe to production lines changes
    const productionLinesChannel = realtimeService?.subscribeToProductionLines((payload) => {
      if (payload?.eventType === 'INSERT') {
        setProductionLines(prev => [...prev, payload?.new]);
      } else if (payload?.eventType === 'UPDATE') {
        setProductionLines(prev => prev?.map(line => 
          line?.id === payload?.new?.id ? payload?.new : line
        ));
      } else if (payload?.eventType === 'DELETE') {
        setProductionLines(prev => prev?.filter(line => line?.id !== payload?.old?.id));
      }
      setLastRefresh(new Date());
    });
    channels?.push(productionLinesChannel);

    // Cleanup subscriptions
    return () => {
      realtimeService?.unsubscribeAll(channels);
    };
  }, []);

  const handleAcknowledgeAlert = (alertId) => {
    if (alertId === 'all') {
      setAlerts([]);
    } else {
      setAlerts(alerts?.filter(alert => alert?.id !== alertId));
    }
  };

  const handleDismissAlert = (alertId) => {
    if (alertId === 'all') {
      setAlerts([]);
    } else {
      setAlerts(alerts?.filter(alert => alert?.id !== alertId));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      productionLine: 'all',
      shift: 'all',
      status: 'all',
      severity: 'all',
      searchQuery: '',
      minCompletion: '',
      maxCompletion: ''
    });
  };

  const handleSaveView = () => {
    console.log('Saving current view configuration:', filters);
  };

  const handleAreaClick = (area) => {
    setSelectedArea(area);
    console.log('Area selected:', area);
  };

  const handleManualRefresh = () => {
    setLastRefresh(new Date());
  };

  const handleExportReport = () => {
    console.log('Exporting production monitoring report');
  };

  const getFilteredAreas = () => {
    return productionAreas?.filter(area => {
      if (filters?.productionLine !== 'all' && !area?.line?.toLowerCase()?.includes(filters?.productionLine)) {
        return false;
      }
      if (filters?.status !== 'all' && area?.status !== filters?.status) {
        return false;
      }
      if (filters?.searchQuery && !area?.name?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase())) {
        return false;
      }
      if (filters?.minCompletion && area?.completionPercentage < parseInt(filters?.minCompletion)) {
        return false;
      }
      if (filters?.maxCompletion && area?.completionPercentage > parseInt(filters?.maxCompletion)) {
        return false;
      }
      return true;
    });
  };

  const filteredAreas = getFilteredAreas();

  const operationalCount = filteredAreas?.filter(a => a?.status === 'operational')?.length;
  const warningCount = filteredAreas?.filter(a => a?.status === 'warning')?.length;
  const criticalCount = filteredAreas?.filter(a => a?.status === 'critical')?.length;
  const offlineCount = filteredAreas?.filter(a => a?.status === 'offline')?.length;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <QuickActionsToolbar />
      <div style={{ marginLeft: projectId ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' : 'var(--sidebar-width, 240px)' }}>
        <div className="sticky top-0 z-40 bg-card border-b-2 border-border">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4 md:py-5">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10">
                <Icon name="Activity" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                  Real-Time Production Monitoring
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Live factory operations dashboard with automated alerts
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <NotificationCenter />
              <Button
                variant="outline"
                size="sm"
                iconName={autoRefresh ? 'Pause' : 'Play'}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                <span className="hidden sm:inline">
                  {autoRefresh ? 'Pause' : 'Resume'}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={handleManualRefresh}
              >
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                iconName="Download"
                onClick={handleExportReport}
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>
          </div>

          <AlertBanner
            alerts={alerts}
            onAcknowledge={handleAcknowledgeAlert}
            onDismiss={handleDismissAlert}
          />
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 space-y-4 md:space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-2 md:gap-4">
              <div className="flex items-center gap-2 px-3 md:px-4 py-2 bg-success/10 rounded-lg border-2 border-success">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span className="text-xs md:text-sm font-medium text-success">
                  {operationalCount} Operational
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 md:px-4 py-2 bg-warning/10 rounded-lg border-2 border-warning">
                <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
                <span className="text-xs md:text-sm font-medium text-warning">
                  {warningCount} Warning
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 md:px-4 py-2 bg-error/10 rounded-lg border-2 border-error">
                <Icon name="AlertOctagon" size={16} color="var(--color-error)" />
                <span className="text-xs md:text-sm font-medium text-error">
                  {criticalCount} Critical
                </span>
              </div>
              {offlineCount > 0 && (
                <div className="flex items-center gap-2 px-3 md:px-4 py-2 bg-muted rounded-lg border-2 border-border">
                  <Icon name="XCircle" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-xs md:text-sm font-medium text-muted-foreground">
                    {offlineCount} Offline
                  </span>
                </div>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              iconName={showFilters ? 'ChevronUp' : 'Filter'}
              onClick={() => setShowFilters(!showFilters)}
            >
              <span className="hidden sm:inline">
                {showFilters ? 'Hide' : 'Show'} Filters
              </span>
            </Button>
          </div>

          {showFilters && (
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              onSaveView={handleSaveView}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
            {filteredAreas?.map((area) => (
              <ProductionAreaTile
                key={area?.id}
                area={area}
                onClick={handleAreaClick}
              />
            ))}
          </div>

          {filteredAreas?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 md:py-16 lg:py-20">
              <Icon name="Search" size={64} color="var(--color-muted-foreground)" />
              <p className="text-base md:text-lg text-muted-foreground mt-4">
                No production areas match your filters
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                className="mt-4"
              >
                Reset Filters
              </Button>
            </div>
          )}

          <TimelineView milestones={milestones} currentTime={currentTime} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <WorkerAssignmentPanel 
              workers={workers} 
              taskAssignments={taskAssignments} 
              shifts={shifts} 
            />
            <EquipmentConnectivityPanel productionAreas={productionAreas} />
          </div>

          <SystemIntegrationStatus integrations={integrations} />

          <div className="flex items-center justify-center py-4 text-xs md:text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span>
                Auto-refresh {autoRefresh ? 'enabled' : 'disabled'} • Last updated: {lastRefresh?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeProductionMonitoring;